import { BitmapUtils } from '../utils/bitmap.utils'
import { AccessPermissions } from '../schemas/users/enums/access-permissions'
import { CorporatePermissions } from '../schemas/users/enums/corporate-permissions'
import { SitePermissions } from '../schemas/users/enums/site-permissions'
import type { ComputedPermissions } from '../schemas/users/staff-user-schemas'
import type { AccessPermissionsKey } from '../schemas/users/enums/access-permissions'
import type { CorporatePermissionsKey } from '../schemas/users/enums/corporate-permissions'
import type { SitePermissionsKey } from '../schemas/users/enums/site-permissions'
import { RequestContextSchema } from '../backend/context/schemas'
import { AdminUserPermissionsValue } from '../schemas/users/admin-access-group-schemas'

// Types
export enum AccessLevels {
  ACCESS = 'access',
  CORPORATE = 'corporate',
  SITE = 'site',
}

export type PermissionOperator = 'and' | 'or'

export interface IAccessPolicyPermissions {
  access?: AccessPermissionsKey[]
  corporate?: CorporatePermissionsKey[]
  site?: SitePermissionsKey[]
}

export interface StaffUserTypePermissions {
  access?: {
    permissions: AccessPermissionsKey[]
    operator?: PermissionOperator
  }
  corporate?: {
    permissions: CorporatePermissionsKey[]
    operator?: PermissionOperator
  }
  site?: {
    permissions: SitePermissionsKey[]
    operator?: PermissionOperator
  }
}

export interface UserTypePermission<T> {
  permissions: T[]
  operator?: PermissionOperator
}

export interface ClientPermissionConfig {
  /** Enable self-access enforcement. Clients can only access their own data.
   * - `true`: extracts clientId from first positional arg (string UUID) or object field 'clientId'
   * - `'fieldName'`: extracts clientId from the named field on the first arg object */
  selfOnly?: true | string
  /** Self-access OR an ACCEPTED ClientBookingBuddy relationship with the target client.
   * Same extraction semantics as selfOnly. Backend-only (decorator performs a DB lookup
   * when the caller is not the target). */
  selfOrBuddy?: true | string
}

export interface UserTypePermissions {
  admin?: UserTypePermission<string> | true
  core?: StaffUserTypePermissions | true
  client?: true | ClientPermissionConfig
  /** Opt-in for the unauthenticated guest principal (guest checkout bridged through servers/api).
   * Guests are denied everywhere this is absent; the method must still scope its own data to
   * client-less rows. */
  guest?: true
}

// User context interface for dependency injection
export type UserContext = RequestContextSchema['user']

/**
 * Pure authorization service that can be used by both frontend and backend
 * All methods receive the necessary context as parameters
 */
export class AuthorizationService {
  /**
   * Check if user has a specific permission
   */
  static hasPermission(
    computedPermissions: ComputedPermissions,
    permission: string,
    siteId?: string
  ): boolean {
    // Get the permission index from the appropriate enum
    let permissionIndex: bigint | undefined

    // Check in each enum to find the permission
    if ((AccessPermissions as any)[permission] !== undefined) {
      permissionIndex = (AccessPermissions as any)[permission]
      const bitmap = BitmapUtils.deserializeBigInt(computedPermissions.access)
      return BitmapUtils.hasBit(bitmap, permissionIndex!)
    } else if ((CorporatePermissions as any)[permission] !== undefined) {
      permissionIndex = (CorporatePermissions as any)[permission]
      const bitmap = BitmapUtils.deserializeBigInt(computedPermissions.corporate)
      return BitmapUtils.hasBit(bitmap, permissionIndex!)
    } else if ((SitePermissions as any)[permission] !== undefined) {
      permissionIndex = (SitePermissions as any)[permission]
      if (!siteId) {
        // Check if user has this permission for ANY site
        for (const siteBitmapStr of Object.values(computedPermissions.sites)) {
          const bitmap = BitmapUtils.deserializeBigInt(siteBitmapStr)
          if (BitmapUtils.hasBit(bitmap, permissionIndex!)) {
            return true
          }
        }
        return false
      }

      const bitmap = AuthorizationService.getSiteBitmap(computedPermissions, siteId)
      return bitmap !== null && BitmapUtils.hasBit(bitmap, permissionIndex!)
    }

    return false
  }

  static getSiteBitmap(computedPermissions: ComputedPermissions, siteId: string): bigint | null {
    const siteBitmapStr = computedPermissions.sites[siteId]
    const allSitesBitmapStr = computedPermissions.sites['*']
    if (siteBitmapStr === undefined && allSitesBitmapStr === undefined) {
      return null
    }

    return BitmapUtils.mergeBitmaps(
      BitmapUtils.deserializeBigInt(siteBitmapStr ?? '0'),
      BitmapUtils.deserializeBigInt(allSitesBitmapStr ?? '0')
    )
  }

  /**
   * Get all permissions for a specific site
   */
  static getPermissionsForSite(computedPermissions: ComputedPermissions, siteId: string): string[] {
    const bitmap = AuthorizationService.getSiteBitmap(computedPermissions, siteId)
    if (bitmap === null) {
      return []
    }

    const permissions: string[] = []

    for (const [permission, index] of Object.entries(SitePermissions)) {
      if (BitmapUtils.hasBit(bitmap, index)) {
        permissions.push(permission)
      }
    }

    return permissions
  }

  // Staff user authorization methods
  static STAFF_USER = {
    isType(user: UserContext): boolean {
      return user?.type === 'staff'
    },

    checkIsType(user: UserContext): void {
      if (!this.isType(user)) {
        throw new Error('The current user is not a staff user')
      }
    },

    hasAccessLevelPermissions(user: UserContext): boolean {
      const permissions = user?.permissions
      if (!permissions) {
        return false
      }
      // Check if access bitmap has any permissions set
      return BigInt(permissions.access || '0') !== 0n
    },

    checkHasAccessLevelPermissions(user: UserContext): void {
      this.checkIsType(user)
      if (!this.hasAccessLevelPermissions(user)) {
        throw new Error('The current user does not have access level permissions')
      }
    },

    hasCorporateLevelPermissions(user: UserContext): boolean {
      const permissions = user?.permissions
      if (!permissions) {
        return false
      }
      // Check if corporate bitmap has any permissions set
      return BigInt(permissions.corporate || '0') !== 0n
    },

    checkHasCorporateLevelPermissions(user: UserContext): void {
      this.checkIsType(user)
      if (!this.hasCorporateLevelPermissions(user)) {
        throw new Error('The current user does not have corporate level permissions')
      }
    },

    hasSiteLevelPermissionsForSite(user: UserContext, siteId: string): boolean {
      const permissions = user?.permissions
      if (!permissions) {
        return false
      }

      return permissions.sites?.[siteId] !== undefined || permissions.sites?.['*'] !== undefined
    },

    checkHasSiteLevelPermissionsForSite(user: UserContext, siteId: string): void {
      this.checkIsType(user)
      if (!this.hasSiteLevelPermissionsForSite(user, siteId)) {
        throw new Error('The current user does not have access to this location')
      }
    },

    checkTypeAndAccessLevel(user: UserContext, accessLevel: AccessLevels, siteId?: string): void {
      this.checkIsType(user)

      if (accessLevel === AccessLevels.ACCESS) {
        this.checkHasAccessLevelPermissions(user)
      } else if (accessLevel === AccessLevels.CORPORATE) {
        this.checkHasCorporateLevelPermissions(user)
      } else if (accessLevel === AccessLevels.SITE && siteId) {
        this.checkHasSiteLevelPermissionsForSite(user, siteId)
      }
    },

    hasPermissions(
      user: UserContext,
      expectedPermissions: StaffUserTypePermissions,
      siteId?: string
    ): boolean {
      const permissions = user?.permissions
      if (!permissions) {
        return false
      }

      const { access, corporate, site } = expectedPermissions

      // Check access permissions
      if (access?.permissions?.length) {
        const hasAccess =
          access.operator === 'and'
            ? access.permissions.every((p) => AuthorizationService.hasPermission(permissions, p))
            : access.permissions.some((p) => AuthorizationService.hasPermission(permissions, p))
        if (hasAccess) {
          return true
        }
      }

      // Check corporate permissions
      if (corporate?.permissions?.length) {
        const hasCorporate =
          corporate.operator === 'and'
            ? corporate.permissions.every((p) => AuthorizationService.hasPermission(permissions, p))
            : corporate.permissions.some((p) => AuthorizationService.hasPermission(permissions, p))
        if (hasCorporate) {
          return true
        }
      }

      // Check site permissions
      if (site?.permissions?.length) {
        const hasSite =
          site.operator === 'and'
            ? site.permissions.every((p) =>
                AuthorizationService.hasPermission(permissions, p, siteId)
              )
            : site.permissions.some((p) =>
                AuthorizationService.hasPermission(permissions, p, siteId)
              )
        if (hasSite) {
          return true
        }
      }

      return false
    },

    checkPermissions(
      user: UserContext,
      expectedPermissions: StaffUserTypePermissions,
      siteId?: string
    ): void {
      this.checkIsType(user)

      if (!this.hasPermissions(user, expectedPermissions, siteId)) {
        throw new Error('Staff user missing permissions to perform this action')
      }
    },

    checkPermissionsByAccessLevel(
      user: UserContext,
      accessLevel: AccessLevels,
      expectedPermissions: string[],
      operator: PermissionOperator,
      siteId?: string
    ): void {
      this.checkIsType(user)

      const permissions = user?.permissions
      if (!permissions) {
        throw new Error('No permissions found')
      }

      const hasPermissions =
        operator === 'and'
          ? expectedPermissions.every((p) =>
              AuthorizationService.hasPermission(permissions, p, siteId)
            )
          : expectedPermissions.some((p) =>
              AuthorizationService.hasPermission(permissions, p, siteId)
            )

      if (!hasPermissions) {
        throw new Error(`Staff user missing ${accessLevel} permissions to perform this action`)
      }
    },

    checkPermissionsOrSelf(
      user: UserContext,
      expectedPermissions: StaffUserTypePermissions,
      userIdBeingAccessed: string,
      siteId?: string
    ): void {
      const { id: userId } = user || {}
      const isSelf = userIdBeingAccessed === userId

      if (!isSelf) {
        this.checkPermissions(user, expectedPermissions, siteId)
      }
    },

    siteIdsWithAccess(
      user: UserContext,
      permissionsToFilterBy?: IAccessPolicyPermissions
    ): { siteIds: string[]; isAllSites: boolean } {
      // 'api' principal is corporate-scoped via the gateway brand-DB binding — grant all sites
      // within the bound corporate DB (matches admin behaviour for the public Business API).
      if (user?.type === 'api') {
        return { siteIds: [], isAllSites: true }
      }
      const permissions = user?.permissions
      if (!permissions) {
        return { siteIds: [], isAllSites: false }
      }
      // If no filter, return all sites user has access to
      if (!permissionsToFilterBy) {
        const siteIds = Object.keys(permissions.sites || {})
        const isAllSites = siteIds.includes('*')
        return {
          siteIds: isAllSites ? [] : siteIds,
          isAllSites,
        }
      }

      console.log('🔍 Permissions to filter by:', permissionsToFilterBy)

      // Filter sites based on required permissions
      const siteIds: string[] = []
      for (const [siteId, siteBitmap] of Object.entries(permissions.sites || {})) {
        const hasAllPermissions = this.hasAllPermissionsForSite(
          String(siteBitmap),
          permissionsToFilterBy,
          permissions
        )
        console.log('🔍 Has all permissions:', hasAllPermissions)
        if (hasAllPermissions) {
          if (siteId === '*') {
            console.log('🔍 Is all sites:', true)
            return { siteIds: [], isAllSites: true }
          }
          siteIds.push(siteId)
        }
      }

      console.log('🔍 Site IDs:', siteIds)
      console.log('🔍 Is all sites:', false)
      return { siteIds, isAllSites: false }
    },

    hasAllPermissionsForAllLevels(
      rulePermissions: IAccessPolicyPermissions,
      permissionsToFilterBy: IAccessPolicyPermissions
    ): boolean {
      // Check if rule has all required permissions at each level
      const checkLevel = (
        rulePerms: string[] | undefined,
        filterPerms: string[] | undefined
      ): boolean => {
        if (!filterPerms?.length) {
          return true
        }
        if (!rulePerms?.length) {
          return false
        }
        return filterPerms.every((p) => rulePerms.includes(p))
      }

      return (
        checkLevel(rulePermissions.access, permissionsToFilterBy.access) &&
        checkLevel(rulePermissions.corporate, permissionsToFilterBy.corporate) &&
        checkLevel(rulePermissions.site, permissionsToFilterBy.site)
      )
    },

    hasAllPermissionsForSite(
      siteBitmap: string,
      permissionsToFilterBy: IAccessPolicyPermissions,
      permissions: ComputedPermissions
    ): boolean {
      // Check if site has all required permissions
      const allPermissions = [
        ...(permissionsToFilterBy.access || []),
        ...(permissionsToFilterBy.corporate || []),
        ...(permissionsToFilterBy.site || []),
      ]

      return allPermissions.every((permission) => {
        // Use AuthorizationService to check individual permission
        const mockComputed = {
          ...permissions,
          sites: { check: siteBitmap },
        }
        return AuthorizationService.hasPermission(mockComputed, permission, 'check')
      })
    },
  }

  // Admin user authorization methods
  static ADMIN_USER = {
    isType(user: UserContext): boolean {
      return user?.type === 'admin'
    },

    checkIsType(user: UserContext): void {
      if (!this.isType(user)) {
        throw new Error('The current user is not an admin')
      }
    },

    hasPermissions(
      user: UserContext,
      expectedPermissions: string[],
      operator: PermissionOperator = 'or'
    ): boolean {
      // Admin users still use adminAccess.permissions (unchanged from old system)
      const userPermissions: string[] = user?.adminAccess?.permissions || []

      if (!expectedPermissions.length) {
        return true
      }

      if (operator === 'and') {
        return expectedPermissions.every((p) => userPermissions.includes(p))
      }

      return expectedPermissions.some((p) => userPermissions.includes(p))
    },

    checkPermissions(
      user: UserContext,
      expectedPermissions: AdminUserPermissionsValue[],
      operator: PermissionOperator = 'or'
    ): void {
      this.checkIsType(user)

      if (!this.hasPermissions(user, expectedPermissions, operator)) {
        throw new Error('Admin user missing permissions to perform this action')
      }
    },

    checkPermissionsOrSelf(
      user: UserContext,
      expectedPermissions: AdminUserPermissionsValue[],
      userIdBeingAccessed: string,
      operator: PermissionOperator = 'or'
    ): void {
      const { id: userId } = user || {}
      const isSelf = userIdBeingAccessed === userId

      if (!isSelf) {
        this.checkPermissions(user, expectedPermissions, operator)
      }
    },
  }

  // Client user authorization methods
  static CLIENT_USER = {
    isType(user: UserContext): boolean {
      return user?.type === 'client'
    },

    checkIsType(user: UserContext): void {
      if (!this.isType(user)) {
        throw new Error('The current user is not a client')
      }
    },

    /**
     * Verify the client is accessing their own data.
     * This is the primary authorization mechanism for clients.
     */
    checkIsSelf(user: UserContext, clientIdBeingAccessed: string): void {
      this.checkIsType(user)
      if (user?.id !== clientIdBeingAccessed) {
        throw new Error('Client users can only access their own data')
      }
    },

    isSelf(user: UserContext, clientIdBeingAccessed: string): boolean {
      return this.isType(user) && user?.id === clientIdBeingAccessed
    },
  }

  // API user authorization methods. 'api' = REST API key authenticated request (public Business
  // API), arriving via the gateway's internal service-to-service handshake. It is a trusted,
  // corporate-scoped service principal (the admin key is authenticated upstream at the API layer
  // and the gateway binds the corporate's brand DB), so for data-scoping it behaves like admin.
  static API_USER = {
    isType(user: UserContext): boolean {
      return user?.type === 'api'
    },
  }

  // Guest principal: the unauthenticated guest-checkout caller bridged through servers/api. It
  // carries no permissions and no identity, so it can never be "self" for any record.
  static GUEST_USER = {
    isType(user: UserContext): boolean {
      return user?.type === 'guest'
    },
  }

  // Main entry points
  static checkUserPermissions(user: UserContext, permissionByUserType: UserTypePermissions): void {
    const { type: userType } = user || {}
    let hasPermissions = false

    // 'api' = trusted internal service principal (public Business API / REST API key). It only
    // reaches the subgraphs via the gateway's validated x-internal-service-auth handshake and is
    // already scoped to a single corporate's brand DB via the gateway context. Authorize it for
    // its corporate; data-scoping is handled by siteIdsWithAccess() returning all-sites.
    if (userType === 'api') {
      return
    }

    if (userType === 'guest') {
      if (permissionByUserType.guest === true) {
        return
      }
      throw new Error('Guest users are not permitted to perform this action')
    }

    if (userType === 'staff') {
      const { core: expectedPermissions } = permissionByUserType
      if (expectedPermissions === true) {
        this.STAFF_USER.checkIsType(user)
        return
      } else if (expectedPermissions) {
        hasPermissions = this.STAFF_USER.hasPermissions(user, expectedPermissions)
      }
    } else if (userType === 'admin') {
      const { admin: expectedPermissions } = permissionByUserType
      if (expectedPermissions === true) {
        this.ADMIN_USER.checkIsType(user)
        return
      } else if (expectedPermissions) {
        hasPermissions = this.ADMIN_USER.hasPermissions(
          user,
          typeof expectedPermissions === 'object' ? expectedPermissions.permissions : [],
          typeof expectedPermissions === 'object' ? expectedPermissions.operator : 'or'
        )
      }
    } else if (userType === 'client') {
      const { client: clientPerm } = permissionByUserType
      if (clientPerm === true) {
        // Simple type check - client is authenticated and endpoint allows clients
        this.CLIENT_USER.checkIsType(user)
        return
      } else if (clientPerm) {
        // Client permission config provided - type check is sufficient
        // Self-scoping is enforced at the decorator level, not here
        hasPermissions = this.CLIENT_USER.isType(user)
      }
    }

    if (!hasPermissions) {
      throw new Error('User missing permissions to perform this action')
    }
  }

  static checkUserPermissionsOrSelf(
    user: UserContext,
    permissionByUserType: UserTypePermissions,
    userIdBeingAccessed: string
  ): void {
    const { id: userId } = user || {}
    const isSelf = this.GUEST_USER.isType(user) ? false : userIdBeingAccessed === userId

    if (!isSelf) {
      this.checkUserPermissions(user, permissionByUserType)
    }
  }
}
